import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select from 'react-select';
import { Row, Col, Label, Card, CardHeader, CardBody, Table, Badge, Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, InputGroup, InputGroupText, FormText } from 'reactstrap';
import { apiService } from '../../constants/ApiService';
import { useSelector } from 'react-redux';
import useVendorBranch from '../../utils/useVendorBranch';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { toast } from 'react-toastify';
import conditionalDataLoad from '../../utils/conditionalDataLoad';

// ─── helpers ────────────────────────────────────────────────────────────────
const extractList = (res) => {
  const c = res?.data ?? res;
  return (
    (Array.isArray(c?.data) && c.data) ||
    (Array.isArray(c?.Data) && c.Data) ||
    (Array.isArray(c) && c) ||
    []
  );
};

// compact styles for all filter dropdowns
const selectSm = {
  control: (b) => ({ ...b, minHeight: 31, height: 31, fontSize: '0.78rem' }),
  valueContainer: (b) => ({ ...b, padding: '0 6px' }),
  indicatorsContainer: (b) => ({ ...b, height: 31 }),
  dropdownIndicator: (b) => ({ ...b, padding: '0 4px' }),
  clearIndicator: (b) => ({ ...b, padding: '0 4px' }),
  menu: (b) => ({ ...b, fontSize: '0.78rem', zIndex: 9999 }),
  option: (b) => ({ ...b, padding: '4px 10px' }),
};

// ─── component ──────────────────────────────────────────────────────────────
const StudentVerification = () => {
  // filter state
  const [searchInput, setSearchInput] = useState('');   // raw input (not debounced)
  const [globalSearch, setGlobalSearch] = useState(''); // debounced value sent to API
  const [selectedClassId, setSelectedClassId] = useState('');
  const [batchId, setBatchId] = useState('');
  const [selectedSessionYear, setSelectedSessionYear] = useState('');
  const [verifyFilter, setVerifyFilter] = useState('');
  const [copyLastRemark, setCopyLastRemark] = useState('');

  const [errorMessage, setErrorMessage] = useState({});

  const { user } = useSelector((state) => state.authState || {});
  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } = useVendorBranch(user);

  const [selectionData, setSelectionData] = useState({ vendor_id: '', branch_id: '' });

  // option lists
  const [classOptions, setClassOptions] = useState([]);
  const [batchMasterData, setBatchMasterData] = useState([]);
  const [sessionOptions, setSessionOptions] = useState([]);
  const semesterOptions = Array.from({ length: 10 }, (_, i) => ({
    value: (i + 1),
    label: `Sem ${i + 1}`
  }));

  // table data
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  // modals
  const [remarkModal, setRemarkModal] = useState(false);
  const [newRecordModal, setNewRecordModal] = useState(false);
  const [editMode, setEditMode] = useState(false); // true = editing existing, false = new

  const [activeStudent, setActiveStudent] = useState(null);

  const remarkOptions = [
    { value: "VERIFIED_CORRECT", label: "Verified - All details are correct" },
    { value: "NAME_MISMATCH", label: "Name mismatch" },
    { value: "FATHER_NAME_MISMATCH", label: "Father's name mismatch" },
    { value: "ROLL_NO_MISMATCH", label: "Roll no mismatch" },
    { value: "CLASS_MISMATCH", label: "Class/Course mismatch" },
    { value: "SESSION_MISMATCH", label: "Session mismatch" },
    { value: "INCOMPLETE_DATA", label: "Incomplete data" },
    { value: "INCORRECT_DATA", label: "Incorrect data" },
    { value: "ENROLLED", label: "Enrolled" },
    { value: "NOT_ENROLLED", label: "Not enrolled / Pass-out" },
    { value: "DUPLICATE_RECORD", label: "Possible duplicate record" },
    { value: "CUSTOM", label: "Other / Custom Remark" }
  ];

  const [selectedRemark, setSelectedRemark] = useState('');
  const [remarkText, setRemarkText] = useState('');
  const [lastSemAttended, setLastSemAttended] = useState('');

  const initial = {
    id: 0,
    vendor_id: selectionData.vendor_id,
    branch_id: selectionData.branch_id,
    curr_class_id: 0,
    session_year: '',
    admit_no: '',
    stud_name: '',
    registration_no: '',
    d_o_b: new Date().toISOString().split('T')[0],
    curr_class: '',
    roll_no: '',
    f_name: '',
    stud_ph_no: '',
  };
  const [admissionData, setAdmissionData] = useState(initial);

  // ── debounce search ────────────────────────────────────────────────────────
  const debounceRef = useRef(null);
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setGlobalSearch(val), 450);
  };

  useEffect(() => {
    setAdmissionData((prev) => ({ ...prev, vendor_id: selectionData.vendor_id, branch_id: selectionData.branch_id }));
  }, [selectionData]);

  const onChangeHandle = (e) => {
    const { name, value } = e.target;
    setAdmissionData((prev) => ({ ...prev, [name]: value }));
    setErrorMessage((prev) => ({ ...prev, [name]: '' }));
  };

  const validateInputs = () => {
    const errors = {};
    if (!admissionData.curr_class_id || Number(admissionData.curr_class_id) <= 0)
      errors.curr_class_id = 'Class Name is required';
    if (!admissionData.stud_name) errors.stud_name = 'Student Name is required';
    if (!admissionData.roll_no) errors.roll_no = 'Roll no is required';
    if (!admissionData.f_name) errors.f_name = 'Father Name is required';
    if (!admissionData.session_year) errors.session_year = 'Session year is required';
    if (admissionData.stud_ph_no && !/^[6-9]\d{9}$/.test(admissionData.stud_ph_no))
      errors.stud_ph_no = 'Enter a valid 10-digit phone number';
    if (isSuperAdmin && (!admissionData.vendor_id || Number(admissionData.vendor_id) <= 0)) {
      toast.error('Please select a Vendor'); return false;
    }
    if ((isSuperAdmin || isAdmin || hasMultiBranch) && (!admissionData.branch_id || Number(admissionData.branch_id) <= 0)) {
      toast.error('Please select a Branch'); return false;
    }
    return errors;
  };

  const saveNewRecord = async () => {
    try {
      const errors = validateInputs();
      if (Object.keys(errors).length > 0) { setErrorMessage(errors); return; }
      const formData = new FormData();
      Object.entries(admissionData).forEach(([key, value]) => {
        formData.append(key, value === null || value === undefined ? '' : value);
      });
      const config = { headers: { Accept: 'multipart/form-data', 'Content-Type': 'multipart/form-data' } };
      let response;
      if (admissionData.id !== '0' && admissionData.id !== 0) {
        response = await apiService.put(`api/Admission/UpdateAdmission?id=${admissionData.id}`, formData, config);
      } else {
        response = await apiService.post(`api/Admission/AddAdmission`, formData, config);
      }
      if (response.status === true) {
        toast.success(response.message);
        setNewRecordModal(false);
        setAdmissionData(initial);
        fetchStudents();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.log(error);
      toast.error('SomeThing went wrong!!');
    }
  };

  // ── fetch helpers ──────────────────────────────────────────────────────────
  const fetchClasses = useCallback(async (search = '') => {
    try {
      const res = await apiService.get(
        `api/ClassMaster/GetClassMaster?vendorId=${selectionData?.vendor_id || ''}&branchId=${selectionData?.branch_id || ''}&searchItem=${encodeURIComponent(search)}`
      );
      const list = extractList(res);
      setClassOptions(list.map((x) => ({ value: x.id, label: x.class_Name })).filter((o) => o.value && o.label));
    } catch { setClassOptions([]); }
  }, [selectionData]);

  const fetchBatches = useCallback(async (search = '') => {
    try {
      const res = await apiService.get(
        `api/BatchMaster/GetAllBatch?vendorId=${selectionData?.vendor_id || ''}&branchId=${selectionData?.branch_id || ''}&classId=${selectedClassId}&search=${encodeURIComponent(search)}`
      );
      const list = extractList(res);
      setBatchMasterData(list.map((x) => ({ value: x.id, label: x.batch_name })).filter((o) => o.value && o.label));
    } catch { setBatchMasterData([]); }
  }, [selectionData, selectedClassId]);

  const fetchSessions = useCallback(async (search = '') => {
    try {
      const res = await apiService.get(
        `api/SessionMaster/GetAllSession?vendorId=${selectionData?.vendor_id || ''}&branchId=${selectionData?.branch_id || ''}&search=${encodeURIComponent(search)}`
      );
      const list = extractList(res);
      const opts = list.map((x) => {
        const name = x.session_name ?? x.sessionName ?? x.session_year ?? x.sessionYear ?? '';
        const s = String(name).trim();
        return s ? { value: s, label: s } : null;
      }).filter(Boolean);
      setSessionOptions(Array.from(new Map(opts.map((o) => [o.value, o])).values()));
    } catch { setSessionOptions([]); }
  }, [selectionData]);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.get(
        `api/Enrollment/GetAllEnrollment?vendorId=${selectionData?.vendor_id || ''}&branchId=${selectionData?.branch_id || ''}&classId=${selectedClassId}&batchId=${batchId}&search=${globalSearch}&sessionYear=${encodeURIComponent(selectedSessionYear)}`
      );
      setStudents(extractList(res));
    } catch { setStudents([]); }
    finally { setLoading(false); }
  }, [selectedClassId, batchId, selectedSessionYear, globalSearch, selectionData]);

  // ── effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const canLoad = conditionalDataLoad({
      hasMultiBranch, hasSingleBranch, isSuperAdmin, isAdmin,
      vendorId: selectionData.vendor_id, branchId: selectionData.branch_id,
    });
    if (canLoad) { fetchStudents(); fetchClasses(); fetchBatches(); }
  }, [globalSearch, selectedClassId, selectionData, selectedSessionYear, batchId]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // ── derived list ───────────────────────────────────────────────────────────
  const displayedStudents = students.filter((s) => {
    if (verifyFilter === 'verified') return s.admission.verified === 1;
    if (verifyFilter === 'pending') return s.admission.verified !== 1;
    return true;
  });

  const verifiedCount = students.filter((s) => s.admission.verified === 1).length;
  const unverifiedCount = students.length - verifiedCount;

  // ── open remark/verify modal ───────────────────────────────────────────────
  const openRemark = (student) => {
    setActiveStudent(student);
    setSelectedRemark(student.admission.verification_code ?? '');
    setRemarkText(student.admission.verify_remark ?? '');
    setLastSemAttended(student.admission.last_sem_attended ?? '');
    setRemarkModal(true);
  };

  // ── open edit modal ────────────────────────────────────────────────────────
  const openEditModal = (student) => {
    setEditMode(true);
    setAdmissionData({
      id: student.admission.id,
      vendor_id: student.admission.vendor_id ?? selectionData.vendor_id,
      branch_id: student.admission.branch_id ?? selectionData.branch_id,
      curr_class_id: student.admission.curr_class_id ?? 0,
      curr_class: student.course_name ?? '',
      session_year: student.session_year ?? '',
      admit_no: student.admission.admit_no ?? '',
      stud_name: student.student_name ?? '',
      d_o_b: student.admission.d_o_b ?? new Date().toISOString().split('T')[0],
      roll_no: student.roll_no ?? '',
      registration_no: student.registration_no ?? '',
      f_name: student.admission.f_name ?? '',
      stud_ph_no: student.admission.stud_ph_no ?? '',
    });
    setErrorMessage({});
    setNewRecordModal(true);
  };

  const openNewRecordModal = () => {
    setEditMode(false);
    setAdmissionData({ ...initial, vendor_id: selectionData.vendor_id, branch_id: selectionData.branch_id });
    setErrorMessage({});
    GetLastAdmissionNo();
    setNewRecordModal(true);
  };

  // ── save remark + last_sem_attended ───────────────────────────────────────
  const saveRemark = async () => {
    if (!activeStudent) return;
    if(!selectedRemark){
      toast.error("Verification Remark is required");
      return;
    }
    if(!lastSemAttended){
      toast.error("Last Semester Attended is required");
      return;
    }
    const nextVerified = activeStudent.admission.verified === 1 ? 0 : 1;
    //alert(nextVerified)
    
    setCopyLastRemark(remarkText)
    const payload = {
      verified: nextVerified,
      id: activeStudent.admission.id,
      verify_remark: remarkText,
      verification_code: selectedRemark,
      last_sem_attended: lastSemAttended,
    };
    try {
      await apiService.put(`api/Admission/VerifyAdmission`, payload);
      await fetchStudents();
    } catch (err) { console.error(err); }
    setRemarkModal(false);
    setActiveStudent(null);
    setRemarkText('');
    setSelectedRemark('');
    setLastSemAttended('');
  };

  const GetLastAdmissionNo = async () => {
    try {
      const response = await apiService.get(
        `api/Admission/GetLastAdmissionNo?vendorId=${selectionData.vendor_id}&branchId=${selectionData.branch_id}`,
        { headers: { 'Content-Type': 'application/json', accept: 'application/json' } }
      );
      setAdmissionData((prev) => ({ ...prev, admit_no: response.message }));
    } catch (error) {
      console.log(error);
      toast.error('SomeThing went wrong!!');
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={errorMessage}
        accessButton={true}
      />
      <div className='text-right mb-2'>
        <Button color='primary' onClick={openNewRecordModal}>
          <i className='bi bi-plus-circle' /> New Record
        </Button>
      </div>

      {/* Page header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="mb-0 font-weight-bold text-primary">
            <i className="bi bi-person mr-2" />Student Verification
          </h5>
          <small className="text-muted">Review and verify student records</small>
        </div>
      </div>

      {/* Filter card */}
      <Card className="mb-3 border">
        <CardHeader className="bg-light py-2 px-3">
          <span className="font-weight-bold text-uppercase" style={{ fontSize: '0.78rem', letterSpacing: '0.5px' }}>
            <i className="bi bi-filter mr-2 text-muted" />Search &amp; Filter
          </span>
        </CardHeader>
        <CardBody className="py-2">
          <Row className="align-items-end g-2">
            {/* Search */}
            <Col md="12">
              <InputGroup size="sm">
                <InputGroupText><i className="bi bi-search text-muted" style={{ fontSize: '0.75rem' }} /></InputGroupText>
                <Input
                  bsSize="sm"
                  value={searchInput}
                  onChange={handleSearchChange}
                  placeholder="Name, enrollment no, email..."
                  style={{ fontSize: '0.78rem' }}
                />
              </InputGroup>
            </Col>

            {/* Course */}
            <Col md="3">
              <Label className="mb-1 small font-weight-bold text-secondary" style={{ fontSize: '0.75rem' }}>Course</Label>
              <Select
                styles={selectSm}
                options={classOptions}
                value={classOptions.find((o) => String(o.value) === String(selectedClassId)) || null}
                onChange={(opt) => { const next = opt ? String(opt.value) : ''; setSelectedClassId(next); setBatchId(''); fetchBatches(next, ''); }}
                onInputChange={(val, meta) => { if (meta.action === 'input-change') fetchClasses(val); }}
                isClearable isSearchable
                placeholder="Course..."
              />
            </Col>

            {/* Batch */}
            <Col md="3">
              <Label className="mb-1 small font-weight-bold text-secondary" style={{ fontSize: '0.75rem' }}>Batch</Label>
              <Select
                styles={selectSm}
                options={batchMasterData}
                value={batchMasterData.find((x) => String(x.value) === String(batchId)) || null}
                onChange={(opt) => setBatchId(opt ? String(opt.value) : '')}
                onInputChange={(val, meta) => { if (meta.action === 'input-change') fetchBatches(selectedClassId, val); }}
                isSearchable isClearable
                placeholder="Batch..."
              />
            </Col>

            {/* Session */}
            <Col md="3">
              <Label className="mb-1 small font-weight-bold text-secondary" style={{ fontSize: '0.75rem' }}>Session</Label>
              <Select
                styles={selectSm}
                options={sessionOptions}
                value={sessionOptions.find((o) => String(o.value) === String(selectedSessionYear)) || null}
                onChange={(opt) => setSelectedSessionYear(opt ? String(opt.value) : '')}
                onInputChange={(val, meta) => { if (meta.action === 'input-change') fetchSessions(val); }}
                isClearable isSearchable
                placeholder="Session..."
              />
            </Col>

            {/* Verify Status */}
            <Col md="3">
              <Label className="mb-1 small font-weight-bold text-secondary" style={{ fontSize: '0.75rem' }}>Status</Label>
              <Select
                styles={selectSm}
                options={[
                  { value: '', label: 'All' },
                  { value: 'verified', label: '✓ Verified' },
                  { value: 'pending', label: '⏳ Pending' },
                ]}
                value={
                  verifyFilter === 'verified' ? { value: 'verified', label: '✓ Verified' } :
                    verifyFilter === 'pending' ? { value: 'pending', label: '⏳ Pending' } :
                      { value: '', label: 'All' }
                }
                onChange={(opt) => setVerifyFilter(opt?.value ?? '')}
                placeholder="All"
              />
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Table card */}
      <Card className="border">
        <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom bg-light">
          <div className="d-flex align-items-center" style={{ gap: '16px' }}>
            <small className="text-success">
              <i className="bi bi-circle mr-1" style={{ fontSize: '8px' }} />{verifiedCount} Verified
            </small>
            <small className="text-warning">
              <i className="bi bi-circle mr-1" style={{ fontSize: '8px' }} />{unverifiedCount} Pending
            </small>
          </div>
          <small className="text-muted">{displayedStudents.length} students</small>
        </div>

        <div className="table-responsive compact-table">
          <Table hover className="mb-0">
            <thead className="thead-light">
              <tr>
                <th>#</th>
                <th className="text-center">Actions</th>
                <th>Student</th>
                <th>Roll No</th>
                <th className='text-center'>Enroll Status</th>
                <th>Course</th>
                <th>Session</th>
                <th>Last Sem</th>
                <th>Status</th>
                <th>Verified By</th>
                <th>Code</th>
                <th>Remark</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="text-center py-5 text-muted">
                    <i className="bi bi-spinner fa-spin mr-2" />Loading...
                  </td>
                </tr>
              ) : displayedStudents.length === 0 ? (
                <tr>
                  <td colSpan="12" className="text-center py-5 text-muted">
                    <i className="bi bi-inbox fa-2x d-block mb-2" />No students found
                  </td>
                </tr>
              ) : (
                displayedStudents.map((s, i) => (
                  <tr key={s.student_id}>
                    <td className="text-muted small">{i + 1}</td>

                    {/* Actions */}
                    <td className="text-center">
                      <div className="d-flex justify-content-center" style={{ gap: '4px' }}>
                        {s.admission.verified === 1 && s.admission.verified_by_id === user.id ? (
                          <Button className='btn-sm py-1 px-1' outline color="danger" onClick={() => openRemark(s)} title="Undo verification">
                            <i className="bi bi-x-circle" /> Undo
                          </Button>
                        ) : s.admission.verified === 0 ? (
                          <>
                            <Button className='btn-sm py-1 px-1' outline color="success" onClick={() => openRemark(s)} title="Verify">
                              <i className="bi bi-check-circle" /> Verify
                            </Button>

                            <Button className='btn-sm py-1 px-1' outline color="secondary" onClick={() => openEditModal(s)} title="Edit record">
                              <i className="bi bi-pencil" /> Edit
                            </Button>
                          </>
                        ) : <span className="badge badge-sm py-1 text-white badge-info small">
                          Verified by <br/>{s.admission.verified_by_user}
                        </span>}

                      </div>
                    </td>

                    <td className="font-weight-bold">
                      {s.student_name}<br />
                      <small className="text-muted">Father: {s.admission.f_name ?? "N/A"}</small>
                    </td>
                    <td><code>{s.admission.roll_no}</code><br /><small>{s.admission.registration_no}</small></td>
                    <td className='text-center'>
                      {s.status === "YES" ? (
                        <Badge className="badge-sm py-1 text-white" color="primary" pill><i className="bi bi-check-circle mr-1" />Enrolled</Badge>
                      ) : s.status === "EARLY_DROPOUT" ? (
                        <Badge className="badge-sm py-1 text-white" color="danger" pill><i className="bi bi-clock mr-1" />Early Dropout</Badge>
                      ) : (
                        <Badge className="badge-sm py-1 text-white" color="danger" pill><i className="bi bi-clock mr-1" />{s.status}</Badge>
                      )}
                    </td>
                    <td><small>{s.course_name}</small></td>
                    <td><small>{s.session_year}</small></td>
                    <td><small>{s.admission.last_sem_attended || <span className="text-muted">—</span>}</small></td>

                    <td className='text-center'>
                      {s.admission.verified === 1 ? (
                        <Badge className="badge-sm py-1 text-white" color="success" pill><i className="bi bi-check-circle mr-1" />Verified</Badge>
                      ) : (
                        <Badge className="badge-sm py-1" color="warning" pill><i className="bi bi-clock mr-1" />Pending</Badge>
                      )}
                    </td>

                    <td><small>{s.admission.verified_by_user || <span className="text-muted">—</span>}</small></td>
                    <td><small>{s.admission.verification_code}</small></td>
                    <td>
                      {s.admission.verify_remark ? (
                        <span className="text-muted small" title={s.admission.verify_remark}
                          style={{ maxWidth: 120, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                          {s.admission.verify_remark}
                        </span>
                      ) : <span className="text-muted small">—</span>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* ── Verify / Remark Modal ─────────────────────────────────────────── */}
      <Modal isOpen={remarkModal} toggle={() => setRemarkModal(false)} centered size="md">
        <ModalHeader toggle={() => setRemarkModal(false)}>
          <i className="bi bi-patch-check mr-2 text-secondary" />
          {activeStudent?.admission?.verified === 1 ? 'Undo Verification' : 'Verify Student'}
        </ModalHeader>
        <ModalBody>
          {activeStudent && (
            <p className="text-muted small mb-3">
              <strong>{activeStudent.student_name}</strong>
              &nbsp;·&nbsp;Roll no: {activeStudent.roll_no}
              &nbsp;·&nbsp;{activeStudent.course_name}
            </p>
          )}

          <Label className="small font-weight-bold">Verification Remark</Label>
          <Select
            options={remarkOptions}
            value={remarkOptions.find((o) => o.value === selectedRemark) || null}
            placeholder="Select Remark"
            onChange={(opt) => setSelectedRemark(opt?.value || '')}
          />

          <Label className="small font-weight-bold mt-3">Last Semester Attended</Label>

          <Select
            options={semesterOptions}
            value={semesterOptions.find(
              x => x.value === lastSemAttended
            )}
            onChange={(selected) =>
              setLastSemAttended(selected?.value || "")
            }
          />
          <div className="d-flex justify-content-between align-items-center mt-3">
  <Label className="small font-weight-bold mb-0">
    Additional Remarks
  </Label>

  <Button
    color="link"
    size="sm"
    className="p-0 text-primary"
    onClick={() => setRemarkText(copyLastRemark)}
    title="Copy Last Remark"
  >
    <i className="bi bi-copy me-1"></i>
    Copy Last Remark
  </Button>
</div>
          <Input
            type="textarea"
            rows={3}
            value={remarkText}
            onChange={(e) => {
              setRemarkText(e.target.value) 
            }}
            placeholder="Additional remarks (optional)..."
          />
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setRemarkModal(false)}>
            <i className="bi bi-times mr-1" />Cancel
          </Button>
          <Button color="primary" onClick={saveRemark}>
            <i className="bi bi-check-circle mr-1" />Save
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── New / Edit Admission Modal ────────────────────────────────────── */}
      <Modal isOpen={newRecordModal} toggle={() => setNewRecordModal(false)} centered size="lg">
        <ModalHeader toggle={() => setNewRecordModal(false)}>
          <i className={`bi ${editMode ? 'bi-pencil-square' : 'bi-person-plus'} mr-2 text-secondary`} />
          {editMode ? 'Edit Student Record' : 'New Admission Record'}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md="6" className="mb-3">
              <Label>Student Name</Label>
              <Input type="text" name="stud_name" value={admissionData.stud_name} onChange={onChangeHandle} placeholder="Enter Student Name" />
              {errorMessage.stud_name && <FormText color='danger'>{errorMessage.stud_name}</FormText>}
            </Col>
            <Col md="6" className="mb-3">
              <Label>Roll No.</Label>
              <Input type="text" name="roll_no" value={admissionData.roll_no} onChange={onChangeHandle} placeholder="Enter Roll No." />
              {errorMessage.roll_no && <FormText color='danger'>{errorMessage.roll_no}</FormText>}
            </Col>
            <Col md="6" className="mb-3">
              <Label>Registration No.</Label>
              <Input type="text" name="registration_no" value={admissionData.registration_no ?? ''} onChange={onChangeHandle} placeholder="Enter Registration No." />
              {errorMessage.registration_no && <FormText color='danger'>{errorMessage.registration_no}</FormText>}
            </Col>
            <Col md="6" className="mb-3">
              <Label>Father Name</Label>
              <Input type="text" name="f_name" value={admissionData.f_name} onChange={onChangeHandle} placeholder="Enter Father Name" />
              {errorMessage.f_name && <FormText color='danger'>{errorMessage.f_name}</FormText>}
            </Col>
            <Col md="6" className="mb-3">
              <Label>Course</Label>
              <Select
                options={classOptions}
                value={classOptions.find((o) => String(o.value) === String(admissionData.curr_class_id)) || null}
                onChange={(opt) => setAdmissionData((prev) => ({ ...prev, curr_class_id: opt ? opt.value : 0, curr_class: opt ? opt.label : '' }))}
                onInputChange={(val, meta) => { if (meta.action === 'input-change') fetchClasses(val); }}
                isClearable isSearchable placeholder="Type a Course Name..."
              />
              {errorMessage.curr_class_id && <FormText color='danger'>{errorMessage.curr_class_id}</FormText>}
            </Col>
            <Col md="6" className="mb-3">
              <Label>Session</Label>
              <Select
                options={sessionOptions}
                value={sessionOptions.find((o) => String(o.value) === String(admissionData.session_year)) || null}
                onChange={(opt) => setAdmissionData((prev) => ({ ...prev, session_year: opt ? opt.value : '' }))}
                onInputChange={(val, meta) => { if (meta.action === 'input-change') fetchSessions(val); }}
                isClearable isSearchable placeholder="Type and Choose a Session..."
              />
              {errorMessage.session_year && <FormText color='danger'>{errorMessage.session_year}</FormText>}
            </Col>
            <Col md="6" className="mb-3">
              <Label>Mobile No.</Label>
              <Input type="text" name="stud_ph_no" value={admissionData.stud_ph_no} onChange={onChangeHandle} placeholder="Enter Mobile No." />
              {errorMessage.stud_ph_no && <FormText color='danger'>{errorMessage.stud_ph_no}</FormText>}
            </Col>
            <Col md="6" className="mb-3">
              <Label>Date of Birth</Label>
              <Input type="date" name="d_o_b" value={admissionData.d_o_b} onChange={onChangeHandle} />
              {errorMessage.d_o_b && <FormText color='danger'>{errorMessage.d_o_b}</FormText>}
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => { setAdmissionData(initial); setNewRecordModal(false); }}>
            <i className="bi bi-x-circle mr-1" />Cancel
          </Button>
          <Button color="primary" onClick={saveNewRecord}>
            <i className="bi bi-check-circle mr-1" />{editMode ? 'Update' : 'Save'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default StudentVerification;